/**
 * OpenTelemetry web tracing — makes browser page loads and API calls the
 * root of end-to-end traces: the fetch/XHR instrumentations attach a W3C
 * `traceparent` header to API requests, which ilmversity-node's OTel setup
 * (OTEL_SERVICE=true) continues, so SigNoz shows click → API → DB in one
 * waterfall.
 *
 * Gated by VITE_APP_OTEL=true; coexists with Sentry browser tracing (which
 * uses its own sentry-trace header) and PostHog.
 */
import {OTLPTraceExporter} from '@opentelemetry/exporter-trace-otlp-http'
import {registerInstrumentations} from '@opentelemetry/instrumentation'
import {DocumentLoadInstrumentation} from '@opentelemetry/instrumentation-document-load'
import {FetchInstrumentation} from '@opentelemetry/instrumentation-fetch'
import {XMLHttpRequestInstrumentation} from '@opentelemetry/instrumentation-xml-http-request'
import {resourceFromAttributes} from '@opentelemetry/resources'
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web'

export const initOtel = () => {
  if (import.meta.env.VITE_APP_OTEL !== 'true') return

  const endpoint = import.meta.env.VITE_APP_OTEL_ENDPOINT || 'http://localhost:4318'
  const ratio = Math.min(
    1,
    Math.max(0, parseFloat(import.meta.env.VITE_APP_OTEL_SAMPLE_RATIO || '1') || 1)
  )

  // API hosts that receive the traceparent header (must stay in sync with
  // the backend CORS config — ours reflects requested headers, so any of
  // our API origins are fine)
  const propagateTargets = [
    /^https?:\/\/localhost(:\d+)?\//,
    // any of our api hosts on any TLD (api, api-dev, api-lgs, api-dev-ai, ...)
    /^https?:\/\/api[a-z0-9-]*\.ilmversity\.[a-z.]+\//,
  ]

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      'service.name': 'ilmversity-portal',
      'deployment.environment': import.meta.env.VITE_APP_ENV || 'local',
    }),
    sampler: new ParentBasedSampler({root: new TraceIdRatioBasedSampler(ratio)}),
    spanProcessors: [new BatchSpanProcessor(new OTLPTraceExporter({url: `${endpoint}/v1/traces`}))],
  })

  provider.register()

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({propagateTraceHeaderCorsUrls: propagateTargets}),
      new XMLHttpRequestInstrumentation({propagateTraceHeaderCorsUrls: propagateTargets}),
    ],
  })

  console.info(`[otel] web tracing started (exporting to ${endpoint})`)
}
